import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';

import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { Settings, DollarSign, Layers, Clock, Mail, Bell, Shield, TrendingUp, Save, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import GestionDispositifs from './GestionDispositifs';
import ClientEmailsConfig from './ClientEmailsConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConfigurationGlobale() {
  const { getAuthHeader } = useAuth();
  const [activeSubTab, setActiveSubTab] = useState('dispositifs');
  const [_loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // États pour les paramètres généraux
  const [parametres, setParametres] = useState({
    duree_validite_souscription_defaut: 365,
    delai_traitement_dommage_jours: 30,
    seuil_alerte_expert_dossiers: 10,
    seuil_alerte_cooper_membres: 20,
    email_notification_admin: 'admin@felixia.fr',
    activer_notifications_email: true,
    activer_notifications_sms: false,
    delai_validation_membre_jours: 7,
    taux_remise_paiement_anticipe: 5,
    penalite_retard_pourcentage: 10
  });

  // États pour les tarifs globaux
  const [tarifsGlobaux, setTarifsGlobaux] = useState({
    frais_inscription_membre: 50,
    frais_inscription_cooper: 150,
    commission_plateforme_pourcentage: 10,
    tarif_expert_horaire_min: 50,
    tarif_expert_horaire_max: 150
  });

  useEffect(() => {
    fetchConfiguration();
  }, []);

  const fetchConfiguration = async () => {
    setLoading(true);
    try {
      // Charger la configuration depuis le backend
      const response = await axios.get(`${API}/admin/configuration`, {
        headers: getAuthHeader()
      });
      
      if (response.data.parametres) {
        setParametres(response.data.parametres);
      }
      if (response.data.tarifs_globaux) {
        setTarifsGlobaux(response.data.tarifs_globaux);
      }
    } catch (error) {
      // Ne pas afficher d'erreur si l'endpoint n'existe pas encore
      if (error.response?.status !== 404) {
        toast.error('Erreur lors du chargement de la configuration');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveParametres = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/configuration/parametres`,
        parametres,
        { headers: getAuthHeader() }
      );
      toast.success('Paramètres enregistrés avec succès');
    } catch {
      toast.error('Erreur lors de l\'enregistrement des paramètres');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTarifs = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/configuration/tarifs`,
        tarifsGlobaux,
        { headers: getAuthHeader() }
      );
      toast.success('Tarifs enregistrés avec succès');
    } catch {
      toast.error('Erreur lors de l\'enregistrement des tarifs');
    } finally {
      setSaving(false);
    }
  };

  const handleParametreChange = (key, value) => {
    setParametres(prev => ({ ...prev, [key]: value }));
  };

  const handleTarifChange = (key, value) => {
    setTarifsGlobaux(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Hero Header — Ultra Pro */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 p-6 lg:p-8 text-white shadow-2xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(99,102,241,0.4),_transparent_55%)] pointer-events-none" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,_rgba(56,189,248,0.25),_transparent_45%)] pointer-events-none" />
        <div className="relative flex flex-col xl:flex-row items-start xl:items-center justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 via-indigo-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-indigo-500/40 flex-shrink-0">
              <Settings className="h-7 w-7 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-400/30 backdrop-blur-sm font-semibold text-xs flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Persisté en BDD
                </span>
                <span className="px-2 py-0.5 rounded-full bg-white/10 text-white border border-white/20 backdrop-blur-sm font-semibold text-xs">
                  4 sections
                </span>
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold">Configuration Globale de la Plateforme</h2>
              <p className="text-indigo-100 mt-1 text-sm lg:text-base">
                Pilotez les dispositifs, paramètres, tarifs et emails clients — chaque modification est appliquée à la volée.
              </p>
            </div>
          </div>

          {/* Mini KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 w-full xl:w-auto">
            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-3">
              <div className="text-blue-300 text-[10px] font-semibold uppercase tracking-wider">Frais inscription</div>
              <p className="text-xl font-bold mt-0.5">{tarifsGlobaux.frais_inscription_membre} €</p>
            </div>
            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-3">
              <div className="text-cyan-300 text-[10px] font-semibold uppercase tracking-wider">Commission</div>
              <p className="text-xl font-bold mt-0.5">{tarifsGlobaux.commission_plateforme_pourcentage}%</p>
            </div>
            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-3">
              <div className="text-emerald-300 text-[10px] font-semibold uppercase tracking-wider">Validité défaut</div>
              <p className="text-xl font-bold mt-0.5">{parametres.duree_validite_souscription_defaut} j</p>
            </div>
            <div className="rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 p-3">
              <div className="text-yellow-300 text-[10px] font-semibold uppercase tracking-wider">Notifs email</div>
              <p className="text-xl font-bold mt-0.5">{parametres.activer_notifications_email ? 'ON' : 'OFF'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs principales */}
      <Tabs value={activeSubTab} onValueChange={setActiveSubTab}>
        <TabsList className="grid w-full grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <TabsTrigger value="dispositifs" className="flex items-center gap-2">
            <Layers className="h-4 w-4" />
            Dispositifs & Tarifs
          </TabsTrigger>
          <TabsTrigger value="parametres" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Paramètres Généraux
          </TabsTrigger>
          <TabsTrigger value="tarifs-globaux" className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" />
            Tarifs Globaux
          </TabsTrigger>
          <TabsTrigger value="emails-clients" className="flex items-center gap-2">
            <Mail className="h-4 w-4" />
            Emails Clients
          </TabsTrigger>
        </TabsList>

        {/* Onglet Dispositifs */}
        <TabsContent value="dispositifs" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-blue-600" />
                Gestion des Dispositifs
              </CardTitle>
              <CardDescription>
                Créez et gérez les dispositifs d'assurance (RCD, BATICO, etc.) avec leurs tarifs spécifiques
              </CardDescription>
            </CardHeader>
            <CardContent>
              <GestionDispositifs />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onglet Paramètres Généraux */}
        <TabsContent value="parametres" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Durées et Délais */}
            <Card>
              <CardHeader className="bg-blue-50">
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <Clock className="h-5 w-5" />
                  Durées et Délais
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="duree-souscription">
                    Durée de validité souscription par défaut (jours)
                  </Label>
                  <Input
                    id="duree-souscription"
                    type="number"
                    value={parametres.duree_validite_souscription_defaut}
                    onChange={(e) => handleParametreChange('duree_validite_souscription_defaut', parseInt(e.target.value))}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="delai-traitement">
                    Délai traitement dommage (jours)
                  </Label>
                  <Input
                    id="delai-traitement"
                    type="number"
                    value={parametres.delai_traitement_dommage_jours}
                    onChange={(e) => handleParametreChange('delai_traitement_dommage_jours', parseInt(e.target.value))}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="delai-validation">
                    Délai validation membre (jours)
                  </Label>
                  <Input
                    id="delai-validation"
                    type="number"
                    value={parametres.delai_validation_membre_jours}
                    onChange={(e) => handleParametreChange('delai_validation_membre_jours', parseInt(e.target.value))}
                    className="mt-2"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Seuils d'Alerte */}
            <Card>
              <CardHeader className="bg-orange-50">
                <CardTitle className="flex items-center gap-2 text-orange-900">
                  <AlertCircle className="h-5 w-5" />
                  Seuils d'Alerte
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="seuil-expert">
                    Seuil alerte dossiers par expert
                  </Label>
                  <Input
                    id="seuil-expert"
                    type="number"
                    value={parametres.seuil_alerte_expert_dossiers}
                    onChange={(e) => handleParametreChange('seuil_alerte_expert_dossiers', parseInt(e.target.value))}
                    className="mt-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Alerte si un expert dépasse ce nombre de dossiers en cours
                  </p>
                </div>

                <div>
                  <Label htmlFor="seuil-cooper">
                    Seuil alerte membres par COOPER'S
                  </Label>
                  <Input
                    id="seuil-cooper"
                    type="number"
                    value={parametres.seuil_alerte_cooper_membres}
                    onChange={(e) => handleParametreChange('seuil_alerte_cooper_membres', parseInt(e.target.value))}
                    className="mt-2"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Alerte si un COOPER'S dépasse ce nombre de membres assignés
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Notifications */}
            <Card>
              <CardHeader className="bg-purple-50">
                <CardTitle className="flex items-center gap-2 text-purple-900">
                  <Bell className="h-5 w-5" />
                  Notifications
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="email-admin">
                    Email administrateur principal
                  </Label>
                  <Input
                    id="email-admin"
                    type="email"
                    value={parametres.email_notification_admin}
                    onChange={(e) => handleParametreChange('email_notification_admin', e.target.value)}
                    className="mt-2"
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-slate-600" />
                    <div>
                      <p className="font-medium">Notifications Email</p>
                      <p className="text-xs text-slate-500">Activer les emails automatiques</p>
                    </div>
                  </div>
                  <Switch
                    checked={parametres.activer_notifications_email}
                    onCheckedChange={(checked) => handleParametreChange('activer_notifications_email', checked)}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Bell className="h-5 w-5 text-slate-600" />
                    <div>
                      <p className="font-medium">Notifications SMS</p>
                      <p className="text-xs text-slate-500">Activer les SMS automatiques</p>
                    </div>
                  </div>
                  <Switch
                    checked={parametres.activer_notifications_sms}
                    onCheckedChange={(checked) => handleParametreChange('activer_notifications_sms', checked)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Remises et Pénalités */}
            <Card>
              <CardHeader className="bg-green-50">
                <CardTitle className="flex items-center gap-2 text-green-900">
                  <TrendingUp className="h-5 w-5" />
                  Remises et Pénalités
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="remise">
                    Taux remise paiement anticipé (%)
                  </Label>
                  <Input
                    id="remise"
                    type="number"
                    value={parametres.taux_remise_paiement_anticipe}
                    onChange={(e) => handleParametreChange('taux_remise_paiement_anticipe', parseFloat(e.target.value))}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="penalite">
                    Pénalité retard paiement (%)
                  </Label>
                  <Input
                    id="penalite"
                    type="number"
                    value={parametres.penalite_retard_pourcentage}
                    onChange={(e) => handleParametreChange('penalite_retard_pourcentage', parseFloat(e.target.value))}
                    className="mt-2"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Bouton Enregistrer Paramètres */}
          <div className="flex justify-end">
            <Button
              onClick={handleSaveParametres}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
              size="lg"
            >
              <Save className="mr-2 h-5 w-5" />
              {saving ? 'Enregistrement...' : 'Enregistrer les Paramètres'}
            </Button>
          </div>
        </TabsContent>

        {/* Onglet Tarifs Globaux */}
        <TabsContent value="tarifs-globaux" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Frais d'Inscription */}
            <Card>
              <CardHeader className="bg-blue-50">
                <CardTitle className="flex items-center gap-2 text-blue-900">
                  <DollarSign className="h-5 w-5" />
                  Frais d'Inscription
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="frais-membre">
                    Frais inscription membre (€)
                  </Label>
                  <Input
                    id="frais-membre"
                    type="number"
                    step="0.01"
                    value={tarifsGlobaux.frais_inscription_membre}
                    onChange={(e) => handleTarifChange('frais_inscription_membre', parseFloat(e.target.value))}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="frais-cooper">
                    Frais inscription COOPER'S (€)
                  </Label>
                  <Input
                    id="frais-cooper"
                    type="number"
                    step="0.01"
                    value={tarifsGlobaux.frais_inscription_cooper}
                    onChange={(e) => handleTarifChange('frais_inscription_cooper', parseFloat(e.target.value))}
                    className="mt-2"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Commission et Tarifs Experts */}
            <Card>
              <CardHeader className="bg-green-50">
                <CardTitle className="flex items-center gap-2 text-green-900">
                  <Shield className="h-5 w-5" />
                  Commissions et Experts
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div>
                  <Label htmlFor="commission">
                    Commission plateforme (%)
                  </Label>
                  <Input
                    id="commission"
                    type="number"
                    step="0.1"
                    value={tarifsGlobaux.commission_plateforme_pourcentage}
                    onChange={(e) => handleTarifChange('commission_plateforme_pourcentage', parseFloat(e.target.value))}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="tarif-min-expert">
                    Tarif horaire expert minimum (€)
                  </Label>
                  <Input
                    id="tarif-min-expert"
                    type="number"
                    step="1"
                    value={tarifsGlobaux.tarif_expert_horaire_min}
                    onChange={(e) => handleTarifChange('tarif_expert_horaire_min', parseFloat(e.target.value))}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="tarif-max-expert">
                    Tarif horaire expert maximum (€)
                  </Label>
                  <Input
                    id="tarif-max-expert"
                    type="number"
                    step="1"
                    value={tarifsGlobaux.tarif_expert_horaire_max}
                    onChange={(e) => handleTarifChange('tarif_expert_horaire_max', parseFloat(e.target.value))}
                    className="mt-2"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Résumé des tarifs */}
          <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200">
            <CardContent className="pt-6">
              <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-600" />
                Résumé des Tarifs Globaux
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-xs text-slate-500 mb-1">Membre</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {tarifsGlobaux.frais_inscription_membre}€
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-xs text-slate-500 mb-1">COOPER'S</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {tarifsGlobaux.frais_inscription_cooper}€
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-xs text-slate-500 mb-1">Commission</p>
                  <p className="text-2xl font-bold text-green-600">
                    {tarifsGlobaux.commission_plateforme_pourcentage}%
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-xs text-slate-500 mb-1">Expert min/h</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {tarifsGlobaux.tarif_expert_horaire_min}€
                  </p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <p className="text-xs text-slate-500 mb-1">Expert max/h</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {tarifsGlobaux.tarif_expert_horaire_max}€
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Bouton Enregistrer Tarifs */}
          <div className="flex justify-end">
            <Button
              onClick={handleSaveTarifs}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
              size="lg"
            >
              <Save className="mr-2 h-5 w-5" />
              {saving ? 'Enregistrement...' : 'Enregistrer les Tarifs'}
            </Button>
          </div>
        </TabsContent>

        {/* Onglet Emails Clients */}
        <TabsContent value="emails-clients" className="space-y-4">
          <ClientEmailsConfig getAuthHeader={getAuthHeader} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
